import { WEEK_DAYS } from 'Utils/dates'
import { getUnixTime, addDays, parseISO } from 'date-fns'
import { toPercentage } from 'Utils/number'

import { TREND_CATEGORIES, DATA_INDEX, ALLOWED_THREAT_GROUPS_NAMES } from './constants'

import {
  DashboardSummaryDataEntry,
  DatedAndDomainReducedTrendsAndThreatGroups,
  ReducedResult,
  ThreatGroupData,
  ThreatGroupDrillDownPart,
  InvalidVisitsTrendsAndThreatGroupsBotzappingData as BotzappingData,
  Filters,
  ReducedTrendsAndThreatGroups,
  reducedTrendsAndThreatGroupsBuilder,
} from './types'
import { RISKS, CRITICAL_PERCENTAGE, HIGH_PERCENTAGE, MEDIUM_PERCENTAGE, Risk } from 'Types/Product'

export type DetailedViewRequest = {
  accountId: number
  userId: number
} & Filters

export const DEFAULT_TIME_ZONE = 'UTC'

export const defaultFilters: Filters = {
  endDate: getUnixTime(new Date()),
  startDate: getUnixTime(addDays(new Date(), -WEEK_DAYS)),
  page: 0,
  keyword: '',
  column: 'last_click',
  order: -1,
  pageType: [],
  campaigns: [],
  threatTypes: [],
  threatGroups: [],
  domains: [],
  countries: [],
  isHomePage: true,
  timezone: DEFAULT_TIME_ZONE,
}

const updateCategoryVisits = <T extends Pick<ReducedTrendsAndThreatGroups, 'VISITS'>>(
  category: T,
  { unixTimestamp, visits = 0 }: Pick<DashboardSummaryDataEntry, 'unixTimestamp' | 'visits'>
) => {
  if (unixTimestamp) {
    const visitsList = category[TREND_CATEGORIES.VISITS][unixTimestamp]
    if (visitsList) {
      visitsList[DATA_INDEX.VALUE] += visits
    } else {
      category[TREND_CATEGORIES.VISITS][unixTimestamp] = [unixTimestamp, visits]
    }
  }
}

const updateVisits = (
  accumulator: DatedAndDomainReducedTrendsAndThreatGroups,
  current: Pick<DashboardSummaryDataEntry, 'domain' | 'unixTimestamp' | 'visits'>
) => {
  updateCategoryVisits(accumulator.byDate, current)
  if (current.domain) {
    const byDomain = accumulator.byDomain[current.domain]
    if (byDomain) updateCategoryVisits(byDomain, current)
    else {
      accumulator.byDomain[current.domain] = reducedTrendsAndThreatGroupsBuilder.byDomain()
      updateCategoryVisits(accumulator.byDomain[current.domain], current)
    }
  }
}

const updateInvalidRate = (
  accumulator: DatedAndDomainReducedTrendsAndThreatGroups,
  unixTimestamp: number,
  invalid: number,
  paidTraffic = 0
) => {
  const visitsList = accumulator.byDate[TREND_CATEGORIES.INVALID_RATE][unixTimestamp]
  if (visitsList) {
    visitsList.paidTraffic += paidTraffic
    visitsList.invalid += invalid
  } else {
    accumulator.byDate[TREND_CATEGORIES.INVALID_RATE][unixTimestamp] = {
      paidTraffic,
      invalid,
      get rate() {
        return toPercentage(this.paidTraffic, this.invalid)
      },
    }
  }
}

const updateCategoryThreatGroups: <
  T extends { threatGroup?: string; threatType?: string },
  K extends keyof T
>(
  category: Pick<ReducedTrendsAndThreatGroups, 'threatGroupsBreakdown'>,
  current: T,
  property: T[K] extends number ? K : never
) => void = (category, current, property) => {
  const breakdown =
    !!current.threatGroup &&
    category.threatGroupsBreakdown[
      current.threatGroup.toLocaleLowerCase() as ALLOWED_THREAT_GROUPS_NAMES
    ]
  if (current.threatGroup && current.threatType && breakdown) {
    const value = current[property] as number
    breakdown.y += value
    breakdown.drilldownData.dataObj[current.threatType] =
      (breakdown.drilldownData.dataObj[current.threatType] || 0) + value
  }
}

const updateThreatGroups: <
  T extends { threatGroup?: string; threatType?: string; domain?: string },
  K extends keyof T
>(
  accumulator: DatedAndDomainReducedTrendsAndThreatGroups,
  current: T,
  property: T[K] extends number ? K : never
) => void = (accumulator, current, property) => {
  updateCategoryThreatGroups(accumulator.byDate, current, property)
  if (current.domain) {
    const byDomain = accumulator.byDomain[current.domain]
    if (byDomain) updateCategoryThreatGroups(byDomain, current, property)
    else {
      accumulator.byDomain[current.domain] = reducedTrendsAndThreatGroupsBuilder.byDomain()
      updateCategoryThreatGroups(accumulator.byDomain[current.domain], current, property)
    }
  }
}

const updateThreatGroupsBreakdownDrilldown = (threatGroupData: Array<ThreatGroupData>) => {
  const result = threatGroupData.reduce(
    (accumulator, current) => {
      if (current.drilldownData.dataObj)
        current.drilldownData.data = Object.entries(current.drilldownData.dataObj)
      if (current.y) {
        accumulator.data.push(current)
        accumulator.drillDownSeries.push(current.drilldownData)
        accumulator.totalThreats += current.y
      }
      return accumulator
    },
    {
      totalThreats: 0,
      data: [] as Array<ThreatGroupData>,
      drillDownSeries: [] as Array<ThreatGroupDrillDownPart>,
    }
  )

  result.data.sort((a, b) => b.y - a.y)
  result.drillDownSeries.forEach((item) =>
    item.data.sort((a, b) => {
      return b[DATA_INDEX.VALUE] - a[DATA_INDEX.VALUE]
    })
  )

  return result
}

export const reducePaidMarketingData: (
  summaryData?: Array<DashboardSummaryDataEntry>
) => ReducedResult = (summaryData = []) => {
  const reduced = summaryData.reduce((accumulator, current) => {
    if (current.unixTimestamp) {
      updateVisits(accumulator, {
        domain: current.domain,
        unixTimestamp: current.unixTimestamp,
        visits: current.invalid,
      })
      updateInvalidRate(accumulator, current.unixTimestamp, current.invalid, current.paidTraffic)
      updateThreatGroups(accumulator, current, 'invalid')
    }

    return accumulator
  }, new DatedAndDomainReducedTrendsAndThreatGroups())

  const result = {
    [TREND_CATEGORIES.INVALID_RATE]: Object.entries(
      reduced.byDate[TREND_CATEGORIES.INVALID_RATE]
    ).map<[number, number]>((entry) => {
      return [Number(entry[DATA_INDEX.DATE]), entry[DATA_INDEX.VALUE].rate]
    }),
    [TREND_CATEGORIES.VISITS]: Object.values(reduced.byDate[TREND_CATEGORIES.VISITS]),
    threatGroupsBreakdown: updateThreatGroupsBreakdownDrilldown(
      Object.values(reduced.byDate.threatGroupsBreakdown)
    ),
    byDomain: Object.entries(reduced.byDomain).reduce((accumulator, [key, value]) => {
      accumulator[key] = {
        ...updateThreatGroupsBreakdownDrilldown(Object.values(value.threatGroupsBreakdown)),
        visits: Object.values(value.VISITS),
      }
      return accumulator
    }, {} as ReducedResult['byDomain']),
  }
  return result
}

export const reduceBotzappingData: (data?: BotzappingData) => ReducedResult = (
  data = { datedSummary: [], insights: [] }
) => {
  const reduced = {
    [TREND_CATEGORIES.INVALID_RATE]: [] as Array<[number, number]>,
    [TREND_CATEGORIES.VISITS]: [] as Array<[number, number]>,
    threatGroupsBreakdown: {
      totalThreats: 0,
      data: [],
      drillDownSeries: [],
    },
    byDomain: {} as DatedAndDomainReducedTrendsAndThreatGroups['byDomain'],
  }

  data.datedSummary?.forEach((item) => {
    const parsedUnixDate = getUnixTime(parseISO(item.date))
    reduced[TREND_CATEGORIES.INVALID_RATE].unshift([parsedUnixDate, item.invalidRate])
    reduced[TREND_CATEGORIES.VISITS].unshift([parsedUnixDate, item.totalBlocked])
    if (item.domain) {
      const byDomain = reduced.byDomain[item.domain]
      if (byDomain)
        updateCategoryVisits(byDomain, { unixTimestamp: parsedUnixDate, visits: item.totalBlocked })
      else {
        reduced.byDomain[item.domain] = reducedTrendsAndThreatGroupsBuilder.byDomain()
        updateCategoryVisits(reduced.byDomain[item.domain], {
          unixTimestamp: parsedUnixDate,
          visits: item.totalBlocked,
        })
      }
    }
  })

  const datedAndDomainReducedTrendsAndThreatGrouped =
    new DatedAndDomainReducedTrendsAndThreatGroups()
  data.insights?.forEach((item) =>
    updateThreatGroups(datedAndDomainReducedTrendsAndThreatGrouped, item, 'totalBlocked')
  )

  const result = {
    [TREND_CATEGORIES.INVALID_RATE]: reduced[TREND_CATEGORIES.INVALID_RATE],
    [TREND_CATEGORIES.VISITS]: reduced[TREND_CATEGORIES.VISITS],
    threatGroupsBreakdown: updateThreatGroupsBreakdownDrilldown(
      Object.values(datedAndDomainReducedTrendsAndThreatGrouped.byDate.threatGroupsBreakdown)
    ),
    byDomain: Object.entries(reduced.byDomain).reduce((accumulator, [key, value]) => {
      accumulator[key] = {
        ...updateThreatGroupsBreakdownDrilldown(
          Object.values(
            datedAndDomainReducedTrendsAndThreatGrouped.byDomain[key]?.threatGroupsBreakdown || []
          )
        ),
        visits: Object.values(value.VISITS),
      }
      return accumulator
    }, {} as ReducedResult['byDomain']),
  }
  return result
}

export const getRiskLevel = (invalidRate: number): Risk => {
  if (invalidRate >= CRITICAL_PERCENTAGE) {
    return RISKS.CRITICAL
  }
  if (invalidRate >= HIGH_PERCENTAGE) {
    return RISKS.HIGH
  }
  if (invalidRate >= MEDIUM_PERCENTAGE) {
    return RISKS.MEDIUM
  }
  return RISKS.LOW
}
